'use strict';
/* ==========================================================================
   levels/front.js — step "front" (orientation any): FRONT PAGE (DESIGN.md §5.3). Spot the impossible headline.

   An attempt = 6 front pages of THE DAILY CHECK from content/bank.front.json, drawn by the model
   (Civics.drawFront(json, {seed, avoid}): 5 unique cheats, easier first, + 13 legit headlines; one page is all legit;
   every headline already judged by Civics.allowed(), every cheat's "What rule does it break?" card already a shuffled
   bank instance). Nothing here decides what is allowed: the page's `answer` and each headline's `verdict` do.

   Per page:  drop (the press prints, the page lands on the desk)
           →  spot: tap the impossible headline, or "All legit". A legit headline tapped by mistake gets a pencil tick
              and "That one's allowed: <the rule it rests on>" (Civics.allowed(claim).text); 3 misses and the Judge
              circles it himself (cheat page) or calls the page legit (legit page)
           →  rule (cheat pages): "What rule does it break?", the item's 3 options (shuffled by the bank); a wrong pick
              is crossed out with its `why`, pick again
           →  fix: the Judge's red pencil strikes the headline and writes the item's `fix` (params filled) in the
              margin; his card shows the rule and the page's stars → next page.
   Stars: right headline first try 1 + right rule first try 1; the all-legit page: "All legit" first try = 2.
   of = 12, pass = bank.front.json passStars (8). Game.progress.scores.front = {best, last, of}; first answers under
   Game.progress.answers['front/<key>/headline'] and ['front/<key>']. A pass → "Collect your stamp" (Game.complete);
   leaving a passed attempt without collecting still keeps the stamp.
   Attempt + seed: Game.progress.bank.front = {pool, seed, attempt, ids, prev, open, run: {i, ev, res}}: a reload
   mid-attempt resumes the same page with the same taps (?seed= / window.__bankSeed force a seed, like core/bank.js).

   Portrait phone (Dock.active): the page is a picture on the stage (with every mark), the Judge beside it; the
   headlines, "All legit", the rule options and the Judge's card are dock widgets.
   Keys: 1–3 = headline (numbered on the page), 4 or L = All legit, 1–3 = rule, Enter / Space = next / start.

   QA hooks: sc.phase ('intro' | 'drop' | 'spot' | 'rule' | 'fix' | 'end'), sc.page (index 0–5), sc.paper (the page:
   {cheat, headlines, answer}), sc.pages, sc.seed, sc.attempt, sc.resumed, sc.results, sc.stars, sc.misses, sc.picks,
   sc.answer() (the impossible headline's index, or 'legit'), sc.solution {headline, rule}, sc.begin(fresh),
   sc.tapHeadline(i), sc.legit(), sc.choose(i), sc.next(), sc.collect(), sc.done(); Levels.front.solution (the live
   scene's), Levels.front.zones() (the headline tap rects), Levels.front.preview(ids) (lay a page out from item ids:
   screenshots of the longest headlines). Integration compat: sc.finishStub() = sc.done().
   ========================================================================== */
(function () {
  const STEP = 'front', TITLE = 'Front Page';
  const MAX_MISSES = 3, DROP = 0.6, NEXT_GUARD = 0.3;
  const CV = () => window.Civic;
  const C = () => CV().C;
  const json = () => Game.content.bank_front || { items: [], draw: {} };
  const SLAB = "'Alfa Slab One'", SERIF = "'Playfair Display'", TYPE = "'Special Elite'", HAND = "'Caveat Brush'";
  const INK = '#2b2420', PENCIL = '#5b6475';
  const PG = { x: 730, top: 190, w: 1220, h: 870 };                  // the newspaper page on the desk
  const sfx = (name, alt, o) => { const n = Audio.has(name) ? name : alt; if (n) Audio.play(n, o); };

  /* ---------------- text fitting ---------------- */
  const measure = (s, font) => { g.save(); g.font = font; const w = g.measureText(s).width; g.restore(); return w; };
  function fit(text, w, maxLines, sMax, sMin) {
    let s = sMax, lines;
    for (; s >= sMin; s -= 2) { lines = UI._wrap(text, w, `${s}px ${SLAB}`); if (lines.length <= maxLines && lines.every(l => measure(l, `${s}px ${SLAB}`) <= w)) return { s, lines }; }
    s = sMin; return { s, lines: UI._wrap(text, w, `${s}px ${SLAB}`) };
  }

  /* the page's stories: 0 = the lead (full width), 1 and 2 = the two columns below. Each story: its headline lines
     (woodtype face), its small print, and its tap zone. Cached per page + UI scale. */
  function layoutPage(pg) {
    const IL = PG.x - PG.w / 2 + 46, IW = PG.w - 92, bottom = PG.top + PG.h - 24, top0 = PG.top + 226;
    const hMin = Math.round(Math.max(26, UI.text(24))), smallBase = Math.round(UI.text(25));
    const CW = (IW - 56) / 2;
    let L = null;
    for (let k = 0; k < 8; k++) {
      const ss = Math.max(18, smallBase - k * 2), lh = 1.08;
      const story = (h, x, w, maxL, sMax) => {
        const f = fit(h.headline, w, maxL, sMax, Math.min(sMax, hMin));
        const small = UI._wrap(h.small, w, `italic ${ss}px ${SERIF}`);
        const hh = f.lines.length * f.s * lh, sh = small.length * ss * 1.28;
        return { x, w, cx: x + w / 2, hs: f.s, lines: f.lines, lh: f.s * lh, ss, small, hh, sh, h: hh + 14 + sh };
      };
      const lead = story(pg.headlines[0], IL, IW, 2, 60 - k * 3);
      const cols = [1, 2].map(i => story(pg.headlines[i], IL + (i - 1) * (CW + 56), CW, 4 + (k > 3 ? 1 : 0), 46 - k * 2));
      const sep = top0 + lead.h + 34;
      const need = sep + 26 + Math.max(cols[0].h, cols[1].h) + 60;
      L = { lead, cols, sep, top0, bottom, IL, IW, CW, k };
      if (need <= bottom) break;
    }
    const { lead, cols, sep, bottom: B } = L;
    lead.y = top0; lead.zone = { x: PG.x, y: (top0 - 16 + sep) / 2, w: IW + 20, h: sep - (top0 - 16) };
    cols.forEach(c => { c.y = sep + 26; c.zone = { x: c.cx, y: (sep + 6 + B) / 2, w: c.w + 24, h: B - sep - 6 }; });
    L.stories = [lead, cols[0], cols[1]];
    L.stories.forEach((s, i) => {
      s.i = i;
      s.lineY = s.lines.map((_, j) => s.y + (j + 0.5) * s.lh);
      s.lineW = s.lines.map(l => measure(l, `${s.hs}px ${SLAB}`));
      s.smallY = s.small.map((_, j) => s.y + s.hh + 14 + (j + 0.5) * s.ss * 1.28);
      s.bodyTop = s.y + s.h + 18;
    });
    return L;
  }

  class Front extends Scene {
    get n() { return this.pages ? this.pages.length : 6; }
    get of() { return this.n * 2; }
    get passStars() { return Math.min(this.of, json().passStars || Math.ceil(this.of * 2 / 3)); }
    get paper() { return this.pages ? this.pages[this.page] : null; }
    get card() { const p = this.paper; return p && p.cheat ? p.cheat.card : null; }
    get stars() { return (this.results || []).reduce((a, r) => a + (r ? r.stars : 0), 0); }
    get solution() { const p = this.paper; return p ? { headline: p.answer < 0 ? 'legit' : p.answer, rule: p.cheat ? p.cheat.card.answerIndex : -1 } : null; }
    answer() { const p = this.paper; return p ? (p.answer < 0 ? 'legit' : p.answer) : null; }

    enter() {
      this.phase = 'intro'; this.pages = null; this.page = 0; this.results = []; this.passed = false; this.collected = false;
      this.lays = new Map(); this.pressH = null;
      Audio.play('paper', { gain: 0.5 });
      const st = Game.progress.bank && Game.progress.bank[STEP];
      if (st && st.open && st.run && st.pool === STEP) this.begin();              // an attempt in progress: straight back in
    }
    exit() { this.stopPress(); this.bankStamp(); }
    stopPress() { if (this.pressH) { this.pressH.stop(); this.pressH = null; } }

    /* ---- the attempt (seeded, saved in Game.progress.bank.front like core/bank.js's attempts) ---- */
    begin(fresh = false) {
      this.bankStamp();
      const P_ = Game.progress, S = P_.bank || (P_.bank = {}), st = S[STEP], forced = Bank.seedOverride();
      let seed, prev, no, resumed = false;
      if (!fresh && st && st.open && st.pool === STEP && (forced == null || String(forced) === String(st.seed))) { seed = st.seed; prev = st.prev || []; no = st.attempt || 1; resumed = true; }
      else { seed = forced != null ? forced : Bank.newSeed(); prev = (st && st.ids) || []; no = ((st && st.attempt) || 0) + 1; }
      const d = Civics.drawFront(json(), { seed, avoid: prev });
      const run = resumed && st.run ? st.run : null;
      S[STEP] = { pool: STEP, seed, attempt: no, ids: d.cheats.concat(d.legit), prev, open: true };
      this.pages = d.pages; this.seed = seed; this.attempt = no; this.resumed = resumed;
      this.results = []; this.passed = false; this.collected = false;
      P_.save();
      if (run && this.restore(run)) return;
      this.startPage(0);
    }
    restore(run) {
      try {
        if (!(run.i >= 0 && run.i < this.pages.length)) return false;
        this.results = (run.res || []).slice(0, run.i);
        this.setup(run.i);
        this.pageAt = Game.time - DROP - 1; this.phase = 'spot';
        this.quiet = true;
        for (const [k, v] of run.ev || []) { if (k === 'h') this.tapHeadline(v); else if (k === 'l') this.legit(); else if (k === 'c') this.choose(v); }
        this.quiet = false;
        this.save();
        return true;
      } catch (e) { this.quiet = false; console.warn('[front] could not resume: ' + e.message); return false; }
    }
    setup(i) {
      this.page = i; this.lays.clear();
      this.ev = []; this.misses = 0; this.marks = {}; this.found = -1; this.revealed = false; this.picks = []; this.say = null;
      this.pageAt = Game.time; this.foundAt = this.qAt = this.fixAt = this.pickedAt = null;
      this._rc = this._fc = null;
    }
    startPage(i) {
      this.setup(i); this.phase = 'drop';
      this.save();
      this.stopPress();
      if (!this.quiet) {
        if (Audio.has('press')) this.pressH = Audio.loop('press', { gain: 0.5 });
        else Audio.play('whoosh', { gain: 0.4 });
      }
    }
    save() {
      const S = Game.progress.bank && Game.progress.bank[STEP];
      if (S && S.open && this.phase !== 'end') S.run = { i: this.page, ev: this.ev.slice(), res: this.results.slice() };
      Game.progress.save();
    }
    play(name, alt, o) { if (!this.quiet) sfx(name, alt, o); }

    /* ---- spot the impossible headline ---- */
    tapHeadline(i) {
      const p = this.paper;
      if (this.phase === 'drop') this.phase = 'spot';                               // a tap while it lands: it's landed
      if (this.phase !== 'spot' || !p || !(i >= 0 && i < p.headlines.length) || this.marks[i]) return false;
      this.ev.push(['h', i]);
      const h = p.headlines[i];
      Game.progress.answer(STEP + '/' + this.pageKey() + '/headline', h.headline);   // first try only
      if (i === p.answer) { this.foundIt(i, false); this.save(); return true; }
      this.marks[i] = { at: Game.time };
      this.miss(`That one's allowed: ${h.verdict.text}`);
      this.save();
      return true;
    }
    legit() {
      const p = this.paper;
      if (this.phase === 'drop') this.phase = 'spot';
      if (this.phase !== 'spot' || !p) return false;
      this.ev.push(['l']);
      Game.progress.answer(STEP + '/' + this.pageKey() + '/headline', 'All legit');
      if (p.answer < 0) { this.toFix(this.misses === 0 ? 2 : 0, 0, 0); this.save(); return true; }
      this.miss('Not all legit: one headline on this page can\'t happen under the Constitution.');
      this.save();
      return true;
    }
    miss(text) {
      this.misses++; this.say = { text, at: Game.time };
      this.play('thunk', null, { gain: 0.4, rate: 0.9 });
      if (this.misses >= MAX_MISSES) {
        if (this.paper.answer < 0) { this.say = { text: 'Every headline here can really happen: this page is all legit.', at: Game.time }; this.revealed = true; this.toFix(0, 0, 0); }
        else { this.say = { text: 'Judge O circles it for you.', at: Game.time }; this.foundIt(this.paper.answer, true); }
      }
    }
    foundIt(i, revealed) {
      this.found = i; this.revealed = revealed; this.foundAt = Game.time; this.qAt = Game.time + 0.5;
      if (!revealed) this.say = null;
      this.phase = 'rule';
      this.play('scribble', null, { gain: 0.4 });
    }
    pageKey() { const p = this.paper; return p.cheat ? p.cheat.key : 'legit:' + p.headlines.map(h => h.id).join('+'); }

    /* ---- what rule does it break? ---- */
    choose(i) {
      const q = this.card;
      if (this.phase !== 'rule' || !q || !(i >= 0 && i < q.options.length) || this.picks.includes(i)) return false;
      this.ev.push(['c', i]);
      if (!this.picks.length) Game.progress.answer(STEP + '/' + q.key, q.options[i]);     // first try only
      this.picks.push(i); this.pickedAt = Game.time;
      if (Bank.check(q, i)) {
        const hs = !this.revealed && this.misses === 0 ? 1 : 0, rs = this.picks.length === 1 ? 1 : 0;
        this.play('ding', null, { gain: 0.6 });
        this.toFix(hs + rs, hs, rs);
      } else this.play('thunk', null, { gain: 0.35, rate: 0.8 });
      this.save();
      return true;
    }
    toFix(stars, hs, rs) {
      const p = this.paper;
      this.phase = 'fix'; this.fixAt = Game.time;
      this.results[this.page] = { page: this.page, legit: p.answer < 0, cheat: p.cheat ? p.cheat.id : null, headline: p.cheat ? p.cheat.headline : 'ALL LEGIT',
        misses: this.misses, revealed: this.revealed, wrongPicks: p.cheat ? this.picks.filter(k => k !== p.cheat.card.answerIndex).length : 0,
        headlineStar: p.answer < 0 ? stars : hs, ruleStar: p.answer < 0 ? 0 : rs, stars };
      this.play('gavel', 'stamp', { gain: 0.6 });
    }
    next() {
      if (this.phase !== 'fix' || Game.time - this.fixAt < NEXT_GUARD) return false;
      this.play('crumple', 'paper', { gain: 0.5 });
      if (this.page < this.pages.length - 1) this.startPage(this.page + 1);
      else this.finish();
      return true;
    }
    finish() {
      const s = this.stars;
      this.stopPress();
      this.phase = 'end'; this.endAt = Game.time; this.passed = s >= this.passStars;
      const S = Game.progress.bank && Game.progress.bank[STEP];
      if (S) { delete S.run; S.open = false; }                                     // the next attempt draws new papers
      const P_ = Game.progress, prev = P_.scores[STEP];
      P_.scores[STEP] = { best: Math.max(prev && prev.best || 0, s), last: s, of: this.of };
      P_.save();
      Audio.play(this.passed ? 'sparkle' : 'paper');
    }
    bankStamp() {
      if (this.passed && !this.collected) { this.collected = true; Game.progress.stamps[STEP] = true; Game.progress.save(); Game._justStamped = STEP; }
    }
    collect() { if (this.collected) return; this.collected = true; Game.complete(STEP); }
    /* tests only: end the attempt now with the stars so far and stamp the step */
    done() {
      if (!this.pages) this.begin();
      if (this.phase !== 'end') this.finish();
      this.collected = true; Game.complete(STEP);
    }
    finishStub() { this.done(); }
    endButtons() {
      return this.passed
        ? [{ label: 'Collect your stamp', go: () => this.collect() }, { label: 'Try again (new papers)', go: () => this.begin(true) }]
        : [{ label: 'Try again (new papers)', go: () => this.begin(true) }, { label: 'Back to the map', go: () => Game.replace(new Levels.map()) }];
    }

    update() {
      if (this.phase === 'drop' && Game.time - this.pageAt > DROP + 0.1) this.phase = 'spot';
      if (this.pressH && Game.time - this.pageAt > 0.9) this.stopPress();
    }
    keyDown(k) {
      const n = parseInt(k, 10);
      if (this.phase === 'intro') { if (k === 'Enter' || k === ' ') this.begin(); }
      else if (this.phase === 'drop' || this.phase === 'spot') { if (n >= 1 && n <= 3) this.tapHeadline(n - 1); else if (n === 4 || k === 'l' || k === 'L') this.legit(); }
      else if (this.phase === 'rule') { if (n >= 1 && n <= 3) this.choose(n - 1); }
      else if (this.phase === 'fix') { if (k === 'Enter' || k === ' ') this.next(); }
      else if (this.phase === 'end') { if (k === 'Enter') this.endButtons()[0].go(); }
    }

    /* ---------------- drawing ---------------- */
    lay() {
      const key = this.page + '|' + Math.round(UI.text(24)) + '|' + (document.fonts ? document.fonts.status : '');
      let L = this.lays.get(key);
      if (!L) { L = layoutPage(this.paper); this.lays.set(key, L); }
      return L;
    }
    sub() {
      if (!this.pages || this.phase === 'intro' || this.phase === 'end') return '';
      return `page ${this.page + 1} of ${this.n} · ${this.stars} star${this.stars === 1 ? '' : 's'}`;
    }
    draw() {
      const docked = UI.docked();
      g.fillStyle = paperTile(PAL.kraft, 3, 'tex_kraft'); g.fillRect(0, 0, Game.W, Game.H);
      UI.hud(TITLE, this.sub());
      if (this.phase === 'intro') return this.drawIntro(docked);
      if (this.phase === 'end') return this.drawEnd(docked);
      const L = this.lay();
      this.drawPage(L, docked);
      if (docked) { this.drawJudge(1640, 1080, 640); return this.drawDock(); }
      if (this.phase === 'drop' || this.phase === 'spot') this.drawSpot();
      else if (this.phase === 'rule') this.drawRule(L);
      else this.drawFix(L);
    }
    drawJudge(x, y, h) {
      const rise = E.outBack(P(T, this.enteredAt + 0.2, this.enteredAt + 0.7), 1.4);
      Puppet.draw('judge', x, y + (1 - rise) * 500, h, { facing: -1, t: T, env: 0 });
    }

    /* the page: newsprint, the masthead, three stories, and every mark (ticks, circle, strike, the fix in the margin) */
    drawPage(L, docked) {
      const p = this.paper, Cv = CV(), c = C(), t0 = this.pageAt;
      const k = clamp((T - t0) / DROP), dy = -(1 - E.outCubic(k)) * 1150, rot = (1 - k) * -0.06 + Math.sin(k * Math.PI) * 0.012;
      const spot = this.phase === 'spot' || this.phase === 'drop', live = !docked && spot && k >= 1;
      g.save();
      g.translate(PG.x, PG.top + PG.h / 2 + dy); g.rotate(rot); g.translate(-PG.x, -(PG.top + PG.h / 2));
      paperRect(PG.x, PG.top + PG.h / 2, PG.w, PG.h, '#ece3cf', { seed: 700, depth: 1.8, boil: false });
      texScrap('tex_news', PG.x, PG.top + PG.h / 2, PG.w - 8, PG.h - 8, { seed: 701, depth: 0, torn: false, alpha: 0.16 });
      Cv.masthead('THE DAILY CHECK', PG.x, PG.top + 90, PG.w - 90, { size: 84, date: `CORAL RIDGE EDITION · No. ${this.page + 1}`, price: 'PRICE 25 CENTS', t0: -1e6 });
      // the column rule between the two lower stories, and the rule under the lead
      const [lead, c1, c2] = L.stories;
      g.fillStyle = INK; g.fillRect(L.IL, L.sep - 2, L.IW, 3); g.fillRect(L.IL, L.sep + 5, L.IW, 1.5);
      g.fillRect(PG.x - 1, L.sep + 22, 2, L.bottom - L.sep - 30);
      this.hover = -1;
      L.stories.forEach((s, i) => {
        const h = p.headlines[i];
        let hit = { hover: false, clicked: false };
        if (live) hit = UI._hit('fp-h' + this.page + '_' + i, s.zone.x, s.zone.y, s.zone.w, s.zone.h);
        if (hit.hover && !this.marks[i]) { g.save(); g.fillStyle = 'rgba(242,179,61,0.22)'; g.fillRect(s.zone.x - s.zone.w / 2, s.zone.y - s.zone.h / 2, s.zone.w, s.zone.h); g.restore(); this.hover = i; }
        this.drawStory(s, h, i === 0 ? L.sep - 14 : L.bottom - 8);
        if (UI.keys && !docked && spot) this.keyBadge(String(i + 1), s.x + (i === 0 ? 14 : 4), s.y - 4);
        if (hit.clicked) this.tapHeadline(i);
      });
      // a legit headline tapped by mistake: washed out, a pencil tick in the margin and "allowed" across it
      Object.keys(this.marks).forEach(i => {
        const s = L.stories[i], m = this.marks[i], a = clamp((T - m.at) / 0.25), z = s.zone;
        g.save(); g.globalAlpha *= 0.55 * a; g.fillStyle = '#ece3cf'; g.fillRect(z.x - z.w / 2 + 6, z.y - z.h / 2 + 4, z.w - 12, z.h - 8); g.restore();
        inkTick(s.x - 12, s.lineY[0], 0.8, PENCIL, m.at);
        hand('allowed', s.cx, (s.lineY[0] + s.lineY[s.lineY.length - 1]) / 2, Math.round(Math.max(52, s.hs * 1.1)), m.at + 0.1, { color: PENCIL, outline: '#f3ead7', rot: -0.07, seed: 730 + +i, dur: 0.35 });
      });
      if (this.phase === 'rule' || this.phase === 'fix') {
        if (p.answer >= 0) {
          const s = L.stories[p.answer], w = Math.max(...s.lineW), top = s.lineY[0] - s.lh / 2, bot = s.lineY[s.lineY.length - 1] + s.lh / 2;
          const at = this.foundAt || this.fixAt;
          loop(s.cx, (top + bot) / 2, Math.min(w / 2 + 30, PG.w / 2 - 14), (bot - top) / 2 + 12, { w: 5, color: c.brick, seed: 740, prog: clamp((T - at) / 0.45) });
        }
      }
      if (this.phase === 'fix') this.drawMarks(L);
      g.restore();
    }
    drawStory(s, h, zoneBottom) {
      g.save(); g.textBaseline = 'middle'; g.textAlign = 'center'; g.fillStyle = INK;
      g.font = `${s.hs}px ${SLAB}`;
      s.lines.forEach((l, j) => g.fillText(l, s.cx, s.lineY[j] + s.hs * 0.04));
      g.font = `italic ${s.ss}px ${SERIF}`; g.fillStyle = '#3b332c';
      s.small.forEach((l, j) => g.fillText(l, s.cx, s.smallY[j]));
      // the story's body: printed lines of small type (grey bars), the look of a column
      g.fillStyle = 'rgba(43,36,32,0.17)';
      const r = rng(760 + s.i * 13 + this.page * 7);
      for (let y = s.bodyTop; y < zoneBottom - 8; y += 17) { const f = y + 34 > zoneBottom ? 0.4 + r() * 0.3 : 0.86 + r() * 0.14; g.fillRect(s.x + 4, y, (s.w - 8) * f, 6); }
      g.restore();
    }
    keyBadge(txt, x, y) {
      g.save(); g.fillStyle = C().navy; g.beginPath(); g.arc(x, y, 18, 0, TAU); g.fill();
      g.font = `22px ${SLAB}`; g.fillStyle = C().cream; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(txt, x, y + 1); g.restore();
    }
    /* the Judge's red pencil: strike the impossible headline, write the fix beside it; a legit page gets ALL LEGIT */
    drawMarks(L) {
      const p = this.paper, c = C(), t0 = this.fixAt + 0.3;
      if (p.answer < 0) {
        L.stories.forEach((s, i) => inkTick(s.x - 12, s.lineY[0], 0.8, c.navy, t0 + i * 0.15));
        UI.stamp('ALL LEGIT', PG.x + 410, PG.top + 118, { rot: -0.12, s: pop(T, t0 + 0.5, 0.3) * 0.55, color: c.navy });
        return;
      }
      const s = L.stories[p.answer];
      s.lines.forEach((l, j) => {
        const w = s.lineW[j], y = s.lineY[j] + s.hs * 0.05, a = t0 + j * 0.25;
        ink([[s.cx - w / 2 - 12, y + 4], [s.cx + w / 2 + 12, y - 4]], { w: 7, color: c.brick, seed: 770 + j, prog: clamp((T - a) / 0.3), passes: 2, amp: 1.2 });
      });
      if (p.answer >= 0) this.drawFixNote(s, L, t0 + s.lines.length * 0.25 + 0.1);
    }
    drawFixNote(s, L, t0) {
      const c = C(), fs = UI.docked() ? 40 : Math.round(UI.text(36)), f = `${fs}px ${HAND}`, w = Math.min(s.w - 30, 820);
      const lines = UI._wrap(this.paper.cheat.fix, w - 40, f), lh = fs * 1.12, h = lines.length * lh + 26;
      // just under the struck headline, over its small print (a slip laid on the page; it may overlap the next story)
      const y = Math.min(s.y + s.hh + 4, PG.top + PG.h - 12 - h);
      if (T < t0) return;
      const sc = pop(T, t0, 0.25, 1.6);
      g.save(); g.translate(s.cx, y + h / 2); g.rotate(-0.02); g.scale(sc, sc);
      paperRect(0, 0, Math.round(w / 10) * 10, Math.round(h / 4) * 4, '#fbf5e6', { seed: 790, depth: 0.7, boil: false });
      g.restore();
      lines.forEach((l, j) => hand(l, s.cx, y + 13 + (j + 0.5) * lh, fs, t0 + 0.15 + j * 0.5, { color: c.brick, rot: -0.02, seed: 800 + j, dur: 0.5 }));
    }

    /* ---- the right-hand panel (desktop / landscape) ---- */
    panelTop() { const hs = UI.hudSlot(0); return Math.max(150, hs.y + hs.r + 24); }
    drawCard(x, y, w, h, seed) { CV().parchment(x + w / 2, y + h / 2, Math.round(w / 10) * 10, Math.round(h / 10) * 10, { seed, depth: 1.3 }); }
    drawSpot() {
      const X = 1382, W = 500, top = this.panelTop(), c = C(), t0 = this.pageAt;
      const qs = Math.round(UI.text(40)), bs = Math.round(UI.text(30));
      const qL = UI._wrap('Which headline can\'t happen?', W - 60, `${qs}px ${SLAB}`);
      const body = UI._wrap('Tap the impossible headline. Every one allowed? Then the page is all legit.', W - 60, `${bs}px ${SERIF}`);
      const h = 30 + qL.length * qs * 1.15 + 14 + body.length * bs * 1.3 + 26;
      this.drawCard(X, top, W, h, 820);
      text(qL, X + 30, top + 30, qs * 1.15, `${qs}px ${SLAB}`, c.navy);
      text(body, X + 30, top + 30 + qL.length * qs * 1.15 + 14, bs * 1.3, `${bs}px ${SERIF}`, INK);
      const bh = UI.tap(96), by = top + h + 30 + bh / 2;
      if (this.t > 0.2 && UI.button('fp-legit' + this.page, (UI.keys ? '4  ' : '') + 'All legit ✓', X + W / 2, by, { w: 420, h: 96, color: c.navy, size: 48 })) this.legit();
      if (this.say) {
        // the Judge's note (why that headline is allowed / not all legit) on a slip of paper under the button
        const ns = Math.round(UI.text(32)), nl = UI._wrap(this.say.text, W - 60, `${ns}px ${HAND}`), nh = nl.length * ns * 1.15 + 30, ny = by + bh / 2 + 24;
        const sc = pop(T, this.say.at, 0.25, 1.6);
        if (sc > 0) { g.save(); g.translate(X + W / 2, ny + nh / 2); g.rotate(0.01); g.scale(sc, sc); paperRect(0, 0, Math.round(W / 10) * 10, Math.round(nh / 6) * 6, PAL.cream, { seed: 831 + nl.length, depth: 0.9, boil: false }); g.restore(); }
        nl.forEach((l, j) => hand(l, X + 30, ny + 15 + (j + 0.5) * ns * 1.15, ns, this.say.at + 0.1 + j * 0.3, { align: 'left', color: c.brick, seed: 830 + j, dur: 0.3 }));
      }
    }
    /* "What rule does it break?": a card with the options as paper rows; the card moves left over the page when a
       phone's big type doesn't fit beside it (then it repeats the circled headline) */
    ruleCard(L) {
      const key = this.page + '|' + this.picks.length + '|' + Math.round(UI.text(28));
      if (this._rc && this._rc.key === key) return this._rc;
      const q = this.card, top0 = this.panelTop(), p = this.paper;
      const qs = Math.round(UI.text(36)), os = Math.round(UI.text(28)), ws = Math.round(UI.text(30)), hsz = Math.round(UI.text(26));
      const last = this.picks[this.picks.length - 1], why = last != null && last !== q.answerIndex ? q.why[last] : '';
      let R;
      for (const X of [1382, 1200, 1000, 820, 620]) {
        const W = 1890 - X, over = X < PG.x + PG.w / 2 - 20;
        const head = over ? UI._wrap('Circled: ' + p.headlines[p.answer].headline, W - 70, `${hsz}px ${SLAB}`) : [];
        const qL = UI._wrap(q.text, W - 70, `${qs}px ${SLAB}`);
        const opts = q.options.map(o => UI._wrap(o, W - 130, `${os}px ${SERIF}`));
        const rows = opts.map(ls => Math.max(UI.tap(84), ls.length * os * 1.25 + 26));
        const whyL = why ? UI._wrap(why, W - 60, `${ws}px ${HAND}`) : [];
        const hh = head.length * hsz * 1.2 + (head.length ? 12 : 0);
        const h = 26 + hh + qL.length * qs * 1.15 + 16 + rows.reduce((a, b) => a + b, 0) + 16 + whyL.length * ws * 1.15 + (whyL.length ? 16 : 0);
        const top = Math.max(96, Math.min(top0, 1070 - h));
        R = { key, X, W, head, hsz, hh, qL, qs, opts, os, rows, whyL, ws, h, top };
        if (top0 + h <= 1070) break;
      }
      return (this._rc = R);
    }
    drawRule(L) {
      const R = this.ruleCard(L), { X, W, head, hsz, hh, qL, qs, opts, os, rows, whyL, ws, h, top } = R, c = C(), q = this.card, t0 = this.qAt;
      if (T < t0) return;
      this.drawCard(X, top, W, h, 850);
      let y = top + 26;
      if (head.length) { text(head, X + 34, y, hsz * 1.2, `${hsz}px ${SLAB}`, c.brick); y += hh; }
      text(qL, X + 34, y, qs * 1.15, `${qs}px ${SLAB}`, c.navy); y += qL.length * qs * 1.15 + 16;
      opts.forEach((ls, i) => {
        const rh = rows[i], cy = y + rh / 2, crossed = this.picks.includes(i);
        const hit = crossed ? { hover: false, clicked: false } : UI._hit('fp-opt' + this.page + '_' + i, X + W / 2, cy, W - 30, rh - 6);
        paperRect(X + W / 2, cy, Math.round((W - 40) / 10) * 10, Math.round((rh - 10) / 2) * 2, hit.hover ? '#fffaf0' : crossed ? '#e6dcc6' : PAL.cream, { seed: 860 + i, depth: hit.hover ? 1.4 : 0.6, boil: false });
        // the option's number block (woodtype)
        g.save(); g.fillStyle = crossed ? PENCIL : c.navy; g.fillRect(X + 34, cy - 22, 44, 44);
        g.font = `26px ${SLAB}`; g.fillStyle = c.cream; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(String(i + 1), X + 56, cy + 1); g.restore();
        const ty = cy - (ls.length - 1) * os * 1.25 / 2;
        text(ls, X + 96, ty - os * 0.625, os * 1.25, `${os}px ${SERIF}`, crossed ? PENCIL : INK);
        if (crossed) {
          const mw = Math.max(...ls.map(l => measure(l, `${os}px ${SERIF}`)));
          const a = i === this.picks[this.picks.length - 1] ? this.pickedAt : -1e9;
          ink([[X + 90, cy + 10], [X + 104 + mw, cy - 10]], { w: 5, color: c.brick, seed: 880 + i, prog: clamp((T - a) / 0.3), passes: 1 });
        }
        if (hit.clicked) this.choose(i);
        y += rh;
      });
      y += 16;
      whyL.forEach((l, j) => hand(l, X + 30, y + (j + 0.5) * ws * 1.15, ws, this.pickedAt + 0.2 + j * 0.2, { align: 'left', color: c.brick, seed: 890 + j }));
      if (this.revealed && this.say) hand(this.say.text, PG.x, PG.top + PG.h + 4, Math.round(UI.text(30)), this.say.at, { color: c.cream, outline: INK, seed: 895 });
    }
    /* the Judge's card: the verdict, the rule, the fix (when the card covers the page), the stars */
    fixLines() {
      const p = this.paper, r = this.results[this.page] || { stars: 0 }, out = [];
      if (p.answer < 0) {
        out.push(['head', r.stars ? 'All legit. Well read!' : 'It was all legit.']);
        out.push(['rule', 'Every headline on this page is something the Constitution allows.']);
      } else {
        out.push(['head', 'Stop the presses!']);
        out.push(['rule', 'The rule: ' + p.cheat.verdict.text]);
        out.push(['fix', p.cheat.fix]);
      }
      const bits = p.answer < 0 ? [] : [`headline ${r.headlineStar ? 1 : 0}`, `rule ${r.ruleStar ? 1 : 0}`];
      out.push(['stars', `+${r.stars} star${r.stars === 1 ? '' : 's'}` + (bits.length ? ` (${bits.join(', ')})` : '')]);
      return out;
    }
    fixCard() {
      const key = this.page + '|' + Math.round(UI.text(28));
      if (this._fc && this._fc.key === key) return this._fc;
      const F = this.fixLines(), top0 = this.panelTop(), bh = UI.tap(96);
      const S = { head: [Math.round(UI.text(40)), SLAB], rule: [Math.round(UI.text(30)), SERIF], fix: [Math.round(UI.text(32)), HAND], stars: [Math.round(UI.text(34)), HAND] };
      let R;
      for (const X of [1382, 1200, 1000, 820]) {
        const W = 1890 - X, over = X < PG.x + PG.w / 2 - 20;
        const rows = F.filter(([k]) => k !== 'fix' || over).flatMap(([k, t]) => UI._wrap(t, W - 70, `${S[k][0]}px ${S[k][1]}`).map(l => [k, l]));
        const h = rows.reduce((a, [k]) => a + S[k][0] * 1.25, 0) + 50;
        const top = Math.max(96, Math.min(top0, 1080 - 14 - bh - 26 - h));
        R = { key, X, W, rows, h, S, bh, top };
        if (top0 + h + 26 + bh + 14 <= 1080) break;
      }
      return (this._fc = R);
    }
    drawFix() {
      const R = this.fixCard(), { X, W, rows, h, S, bh, top } = R, c = C(), t0 = this.fixAt + 0.2;
      this.drawCard(X, top, W, h, 900);
      let y = top + 26, tt = t0;
      const col = { head: c.brick, rule: INK, fix: c.brick, stars: c.navy };
      rows.forEach(([k, l]) => {
        const s = S[k][0], cy = y + s * 0.62;
        if (k === 'fix' || k === 'stars') hand(l, X + 34, cy, s, tt, { align: 'left', color: col[k], seed: 910 + Math.round(y) % 50, dur: 0.4 });
        else if (T >= tt) { g.save(); g.font = `${s}px ${S[k][1]}`; g.fillStyle = col[k]; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(l, X + 34, cy); g.restore(); }
        tt += 0.12; y += s * 1.25;
      });
      const label = this.page < this.n - 1 ? 'Next page ▶' : 'See my stars ▶';
      if (Game.time - this.fixAt > NEXT_GUARD && UI.button('fp-next', label, X + W / 2, top + h + 26 + bh / 2, { w: 420, h: 96, color: c.brick, size: 46 })) this.next();
    }

    /* ---- portrait: the page is the picture; everything to tap is in the dock ---- */
    drawDock() {
      const p = this.paper, q = this.card, id = this.attempt + '_' + this.page;
      const opts = p.headlines.map(h => h.headline);
      if (this.phase === 'drop' || this.phase === 'spot') {
        Dock.text('fp-how', ['Which headline can\'t happen under the Constitution? Tap it, or tap All legit.'], { cls: 'note' });
        const st = p.headlines.map((h, i) => (this.marks[i] ? 'faded' : ''));
        // the why of a wrong pick as its own line under the options (the collage dock has no style for per-option notes):
        // the latest miss is the red line; an earlier allowed headline keeps its verdict as a note line
        const said = this.say && this.say.text;
        const allowed = p.headlines.map((h, i) => (this.marks[i] && `That one's allowed: ${h.verdict.text}` !== said ? `${i + 1} is allowed: ${h.verdict.text}` : '')).filter(Boolean);
        Dock.choices('fp-h' + id, { options: opts, state: st, onChoose: i => this.tapHeadline(i) });
        Dock.buttons('fp-legit' + id, [{ label: 'All legit ✓', wide: true, onClick: () => this.legit() }]);
        if (allowed.length) Dock.text('fp-allowed' + id + '_' + allowed.length, allowed, { cls: 'note' });
        if (this.say) Dock.text('fp-say' + id + '_' + this.misses, [this.say.text], { cls: 'wrong', reveal: true });
        return;
      }
      if (this.phase === 'rule') {
        Dock.text('fp-found' + id, [(this.revealed ? 'Judge O circled: ' : 'You circled: ') + opts[this.found]], { cls: 'note' });
        const st = q.options.map((o, i) => (this.picks.includes(i) ? 'wrong' : ''));
        // the why of each wrong pick as its own line under the options (the collage dock has no style for per-option notes)
        Dock.choices('fp-rule' + id, { prompt: q.text, options: q.options, state: st, onChoose: i => this.choose(i) });
        const whys = this.picks.filter(i => i !== q.answerIndex).map(i => `Not ${i + 1}: ${q.why[i]}`);
        if (whys.length) Dock.text('fp-why' + id + '_' + this.picks.length, whys, { cls: 'wrong', reveal: true });
        return;
      }
      const F = this.fixLines();
      if (q) Dock.choices('fp-rule' + id, { prompt: q.text, options: q.options, state: q.options.map((o, i) => (i === q.answerIndex ? 'right' : this.picks.includes(i) ? 'wrong' : 'faded')), locked: true });
      Dock.heading('fp-fixh' + id, F[0][1]);
      Dock.text('fp-rule-t' + id, F.filter(([k]) => k === 'rule' || k === 'fix').map(([, t]) => t), { cls: 'note' });
      Dock.text('fp-stars' + id, F.filter(([k]) => k === 'stars').map(([, t]) => t), { cls: 'right', reveal: true });
      Dock.button({ id: 'next', glyph: this.page < this.n - 1 ? 'Next page ▶' : 'See my stars ▶', label: 'next', aria: 'next page', go: true, onClick: () => this.next() });
    }

    introLines() {
      return ['The Daily Check prints three headlines on every page.', 'On most pages, one of them can\'t happen under the Constitution.',
        'Tap it, then name the rule it breaks. No fake? Call the page All legit.', `${this.n} pages. ${this.passStars} of ${this.of} stars to sign it.`];
    }
    drawIntro(docked) {
      const t0 = this.enteredAt, c = C(), Cv = CV(), lines = this.introLines();
      this.drawJudge(docked ? 1600 : 330, 1080, docked ? 700 : 760);
      Cv.woodtype('FRONT PAGE', docked ? 800 : 1150, docked ? 420 : 250, docked ? 110 : 96, { color: c.cream, ink: c.navy, t0: t0 + 0.2, seed: 940 });
      if (docked) {
        Dock.heading('fp-intro', 'Front Page', { cls: 'big' });
        Dock.text('fp-intro', lines);
        Dock.button({ id: 'go', glyph: 'Start ▶', label: 'start', aria: 'Start reading the papers', go: true, color: 'red', onClick: () => this.begin() });
        return;
      }
      const fs = Math.round(UI.text(38));
      const nL = lines.reduce((a, l) => a + UI._wrap(l, 1000, `${fs}px ${SERIF}`).length, 0);
      this.drawCard(620, 340, 1060, nL * fs * 1.45 + 60, 935);
      let y = 400;
      lines.forEach((l, i) => UI._wrap(l, 1000, `${fs}px ${SERIF}`).forEach(tl => {
        if (T >= t0 + 0.5 + i * 0.3) { g.save(); g.font = `${i === 3 ? 'bold ' : ''}${fs}px ${SERIF}`; g.fillStyle = i === 3 ? c.brick : INK; g.textAlign = 'center'; g.textBaseline = 'middle'; g.globalAlpha = clamp((T - t0 - 0.5 - i * 0.3) / 0.3); g.fillText(tl, 1150, y); g.restore(); }
        y += fs * 1.45;
      }));
      if (this.t > 0.8 && UI.button('fp-start', 'Start ▶', 1150, Math.min(990, y + 80), { w: 420, h: 110, color: c.brick, size: 56 })) this.begin();
    }
    drawEnd(docked) {
      const s = this.stars, of = this.of, btns = this.endButtons(), c = C(), Cv = CV();
      const rows = this.pages.map((pg, i) => { const r = this.results[i] || { stars: 0 }; return { r, text: pg.cheat ? pg.cheat.headline : 'ALL LEGIT' }; });
      if (docked) {
        Cv.woodtype(`${s} / ${of}`, 760, 380, 150, { color: c.cream, ink: c.navy, t0: this.endAt + 0.1, seed: 950 });
        UI.stamp(this.passed ? 'PASSED' : 'TRY AGAIN', 760, 720, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.6, color: c.brick });
        this.drawJudge(1640, 1080, 640);
        Dock.heading('fp-end', `Your stars: ${s} / ${of}`, { cls: 'big' });
        Dock.text('fp-end', [this.passed ? 'Passed! Collect your stamp.' : `Not yet: ${this.passStars} stars for the stamp. New papers?`]);
        Dock.list('fp-end', rows.map((x, i) => ({ badge: String(i + 1), color: x.r.stars === 2 ? c.navy : c.brick, lines: [x.text, `${x.r.stars} of 2 stars`] })));
        Dock.text('fp-code', [`Quiz code ${this.seed}`], { cls: 'small' });
        Dock.buttons('fp-endb', btns.map((b, i) => ({ label: b.label, wide: i === 0, onClick: b.go })), { bottom: true });
        return;
      }
      Cv.woodtype(`${s} / ${of}`, 700, 250, 110, { color: c.cream, ink: c.navy, t0: this.endAt + 0.1, seed: 950 });
      UI.stamp(this.passed ? 'PASSED' : 'TRY AGAIN', 1340, 250, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.1, color: c.brick });
      const fs = Math.round(UI.text(30)), rh = Math.max(64, fs * 1.5), bh = UI.tap(100), ch = (rows.length + 1) * rh + 50;
      const cardTop = Math.min(350, 1080 - 40 - bh - 36 - ch), X0 = 220, W = 1480;
      this.drawCard(X0, cardTop, W, ch, 960);
      rows.forEach((x, i) => {
        const y = cardTop + 25 + (i + 0.5) * rh;
        let t = `${i + 1}. ${x.text}`;
        g.save(); g.font = `${fs}px ${SLAB}`;
        if (g.measureText(t).width > W - 360) { while (g.measureText(t + '…').width > W - 360 && t.length > 8) t = t.slice(0, -1); t = t.trimEnd() + '…'; }
        g.fillStyle = x.r.legit ? c.navy : INK; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(t, X0 + 50, y); g.restore();
        hand(`${x.r.stars} of 2`, X0 + W - 60, y, fs * 1.2, this.endAt + 0.4 + i * 0.1, { align: 'right', color: x.r.stars === 2 ? c.navy : c.brick, seed: 970 + i });
      });
      const py = cardTop + 25 + (rows.length + 0.5) * rh, by = Math.min(1080 - 40 - bh / 2, cardTop + ch + 36 + bh / 2);
      hand(this.passed ? 'Passed! Collect your stamp.' : `Not yet: ${this.passStars} stars for the stamp. New papers?`, X0 + W / 2, py, Math.round(UI.text(40)), this.endAt + 0.8, { color: c.brick, seed: 980, dur: 0.5 });
      btns.forEach((b, i) => { if (this.t > 0.2 && UI.button('fp-end' + i, b.label, 700 + i * 520, by, { w: 480, h: 100, color: i === 0 ? c.brick : c.navy, size: 44 })) b.go(); });
      hand(`quiz code ${this.seed}`, 1880, by + bh / 2 + 10 > 1070 ? by - bh / 2 - 20 : by + bh / 2 + 10, 28, this.endAt + 0.6, { align: 'right', color: PAL.ink, seed: 985 });
    }
  }

  /* small drawing helpers */
  function text(lines, x, yTop, lh, font, color) {
    g.save(); g.font = font; g.fillStyle = color; g.textAlign = 'left'; g.textBaseline = 'middle';
    lines.forEach((l, j) => g.fillText(l, x, yTop + (j + 0.5) * lh));
    g.restore();
  }
  /* a loose pencil loop around a block (a squarish ellipse, so it clears the corners of the text) */
  function loop(cx, cy, rx, ry, { w = 5, color, seed = 1, prog = 1 } = {}) {
    const pts = [], n = 64;
    for (let i = 0; i <= n * 1.08; i++) { const a = i / n * TAU - 2.2, c = Math.cos(a), s = Math.sin(a); pts.push([cx + rx * Math.sign(c) * Math.pow(Math.abs(c), 0.45) * (1 + 0.02 * Math.sin(i * 0.7)), cy + ry * Math.sign(s) * Math.pow(Math.abs(s), 0.6)]); }
    ink(pts, { w, color, seed, prog, passes: 1, step: 12, amp: 1.4 });
  }
  function inkTick(x, y, s, color, t0) {
    const pr = clamp((T - t0) / 0.3);
    if (pr <= 0) return;
    ink([[x - 18 * s, y], [x - 4 * s, y + 16 * s], [x + 24 * s, y - 22 * s]], { w: 6, color, seed: Math.round(x + y) % 97 + 1, prog: pr, passes: 1, step: 6 });
  }

  Front.STEP = STEP;
  Front.layout = layoutPage;
  Object.defineProperty(Front, 'solution', { get() { const s = Game.current(); return s instanceof Front ? s.solution : null; } });
  /* the live scene's headline tap zones (tests click them) */
  Front.zones = () => { const s = Game.current(); return s instanceof Front && s.paper ? s.lay().stories.map((st, i) => ({ i, ...st.zone })) : []; };
  /* tests / screenshots: replace the current page with headlines built from these item ids (a cheat among them makes it
     that page's answer) */
  Front.preview = (ids, seed = 1) => {
    const s = Game.current(); if (!(s instanceof Front) || !s.pages) return false;
    const items = json().items, r = Bank.rng(seed);
    const hs = ids.map(id => Civics.headline(items.find(it => it.id === id), r));
    const ans = hs.findIndex(h => h.kind === 'cheat');
    s.pages[s.page] = { cheat: ans >= 0 ? hs[ans] : null, headlines: hs, answer: ans };
    s.lays.clear(); s._rc = s._fc = null;
    return true;
  };
  Levels.front = Front;
})();
